import { invoke } from '@tauri-apps/api/core';

import type { OfflineSyncStatus } from '@/types/offline-recording';
import type { LanguageCode, TranscribeMode, VoiceStyle } from '@heidi/schema/ml-scribe';

export interface CreateOfflineRecordingArgs {
  patientLink?: string;
  serverSessionId?: string;
  templateId?: string;
  dictationType?: string;
  /** Input/capture language. */
  language?: string;
  /** Language the synced note is written in (mirrors the online `output_language_code`). */
  outputLanguage?: string;
  /** Note style — the online recorder's "note style" (backend `voice_style`). */
  noteStyle?: string;
  /** Selected input-device id. */
  microphone?: string;
}

/**
 * One cause's count within a single boot's mid-capture salvage.
 *
 * `cause` mirrors `SalvageSkip::code()` and `disposition` mirrors
 * `Disposition::code()` in `src-tauri/src/offline_recording/recovery.rs`. Both
 * are stable telemetry strings and are deliberately typed as `string` rather
 * than as a union: the taxonomy is append-only in Rust, and a union here would
 * make every new cause a TypeScript change before the counts could be
 * forwarded — which is the coupling that leaves a new failure mode unreported.
 */
export interface OfflineSalvageCauseCount {
  cause: string;
  disposition: string;
  count: number;
}

/** One boot's mid-capture salvage tally, read back after the boot window. */
export interface OfflineSalvageRunReport {
  /** Opaque per-boot correlation id. Identifies nobody across runs. */
  recoveryRun: string;
  occurredAt: string;
  /** Takes the salvage scan considered — the denominator the causes are read against. */
  takesSeen: number;
  salvaged: number;
  /** Ordered by cause code. Empty for a run that saw takes and failed none. */
  causes: OfflineSalvageCauseCount[];
}

/**
 * What a `create` resolved to. Mirrors Rust's `CreateOfflineRecordingResult`.
 *
 * `create` is no longer guaranteed to mint an empty consult: one naming a
 * `serverSessionId` this device already holds a local consult for resolves to
 * that consult instead, which is what stops a relaunched resume minting a
 * duplicate row. That makes the old "the id came back, so it holds nothing"
 * reading wrong, and Rust refuses `'start_fresh'` on a consult with takes — so
 * the create site reports what it got and the start site picks the intent.
 */
export interface CreateOfflineRecordingResult {
  localSessionId: string;
  /**
   * True when this resolved to a consult the device already held, false when it
   * minted a new one. Does NOT decide the capture intent on its own — a reuse
   * can land on an empty shell — but it is the only signal that the app lost
   * track of the local↔server link, so it is reported separately.
   */
  reused: boolean;
  /**
   * Whether that consult already holds at least one take. This is what decides
   * `'resume'` vs `'start_fresh'`. Always false on a freshly minted consult.
   */
  hasCapturedAudio: boolean;
}

/**
 * Editable settings for an existing offline local session. Each field mirrors
 * the matching `CreateOfflineRecordingArgs` field and is optional so callers can
 * patch a single setting at a time.
 */
export interface UpdateOfflineRecordingSettingsArgs {
  /** Typed patient identifier captured in offline-capture mode. */
  patientLink?: string;
  /** Selected note template id. */
  templateId?: string;
  /** Transcribe/dictate mode (backend `transcribe_mode`). */
  dictationType?: TranscribeMode;
  /** Input/capture language. */
  language?: LanguageCode;
  /** Language the synced note is written in (mirrors `output_language_code`). */
  outputLanguage?: LanguageCode;
  /** Note style — the online recorder's "note style" (backend `voice_style`). */
  noteStyle?: VoiceStyle;
  /**
   * What the clinician answered on the patient-consent gate (backend
   * `is_consent`).
   *
   * Not a picker like the fields above — it is the only durable home consent
   * has while the backend is unreachable, and the Rust sync engine reads the
   * stored column at drain time and puts it on the session PATCH. Before this
   * existed the answer lived only in the React Query cache entry for the live
   * session, so an offline consult synced with the backend default.
   *
   * `false` is a real answer and must be sent as one: omitting it leaves the
   * stored value alone, which is the encoding of "never asked". The three-state
   * rule behind that is stated once, on `definedConsent` in
   * `@/utils/offline-recording/offlineSessionStub`.
   *
   * A boolean and nothing else — no name, no reason, no free text — so nothing
   * crossing this boundary can be PHI.
   */
  isConsent?: boolean;
}

/**
 * Why a capture is being started, and therefore whether Rust may add it to a
 * consult that already holds audio.
 *
 * Mirrors `CaptureIntent` in `src-tauri/src/offline_recording/recorder.rs`
 * (`#[serde(rename_all = "snake_case")]`).
 *
 * - `start_fresh` — the caller just created this local session and expects it to
 *   hold nothing. Rust refuses if it already does: that means a reused or
 *   colliding id, and appending would file the encounter into an unrelated
 *   consult.
 * - `resume` — the caller named a consult the app already knows about and means
 *   to add to it.
 *
 * Pick this from WHICH SESSION the caller is naming, never from the elapsed
 * timer or from which button rendered. The clinical case this exists for is a
 * clinician who quits the app between the two halves of an encounter, and
 * nothing in memory survives that.
 */
export type OfflineCaptureIntent = 'start_fresh' | 'resume';

/** Subset of OfflineSyncItem returned by the list command — bridge-level only. */
export interface OfflineRecordingItem {
  localSessionId: string;
  serverSessionId: string | null;
  patientLink: string | null;
  status: OfflineSyncStatus;
  attemptCount: number;
  fileSizeBytes: number | null;
  recordedDurationMs: number | null;
  captureRegion: string;
  createdAt: string;
  lastErrorCode: string | null;
  /** How many recordings this consult holds; its takes are collapsed into one row. */
  recordingCount: number;
  // Durable per-session recorder settings, mirroring the matching
  // `UpdateOfflineRecordingSettingsArgs` fields; `null` where the user never
  // chose. Typed as plain strings rather than the schema enums because they
  // come back from SQLite, so a row written by an older build is not
  // guaranteed to be a currently-valid enum member.
  dictationType: string | null;
  language: string | null;
  outputLanguage: string | null;
  templateId: string | null;
  noteStyle: string | null;
  /**
   * What the clinician answered on the patient-consent gate, from
   * `offline_sessions.is_consent`.
   *
   * Typed as the boolean the column holds rather than as a string like the
   * settings above it, because unlike them there is no vocabulary to outgrow: a
   * row written by an older build carries `null` and nothing else.
   *
   * Three-state, and the third state is load-bearing. `null` means the gate
   * never ran for this consult; `false` means the clinician refused. Every
   * consent reader in the app is a truthiness read of `session.is_consent`, so
   * the two currently behave identically — which is exactly why the wire must
   * keep them apart rather than let a falsy collapse decide a clinical question.
   */
  isConsent: boolean | null;
}

/**
 * Emitted by Rust once per accepted server-delete batch. Pinned as a constant
 * on both sides — `SERVER_DELETE_ENQUEUED_EVENT` in
 * `src-tauri/src/offline_recording/commands.rs` — so a rename cannot silently
 * leave a listener attached to a name nothing emits.
 */
export const OFFLINE_SERVER_DELETE_ENQUEUED_EVENT =
  'offline_recording:server_delete_enqueued';

/** Payload of {@link OFFLINE_SERVER_DELETE_ENQUEUED_EVENT}. */
export interface OfflineServerDeleteEnqueuedEvent {
  /**
   * The server session ids just queued. Opaque identifiers only — no title,
   * patient link or note text crosses this boundary.
   */
  serverSessionIds: string[];
}

/**
 * Emitted by Rust once per queued server delete that has stopped being owed,
 * either way. Pinned as a constant on both sides — `SERVER_DELETE_SETTLED_EVENT`
 * in `src-tauri/src/offline_recording/sync/delete_pass.rs`.
 */
export const OFFLINE_SERVER_DELETE_SETTLED_EVENT =
  'offline_recording:server_delete_settled';

/** Payload of {@link OFFLINE_SERVER_DELETE_SETTLED_EVENT}. */
export interface OfflineServerDeleteSettledEvent {
  /** Opaque server identifier. No title, patient link or note text crosses. */
  serverSessionId: string;
  /**
   * `replayed` when the backend has the delete, so the list can be re-fetched
   * before the id is dropped from the render-time mirror. `failed` when the
   * delete will never happen and the session comes back.
   */
  outcome: 'replayed' | 'failed';
  /**
   * Why it was given up: one of the terminal `OFFLINE_DELETE_*` codes
   * (`FORBIDDEN`, `ATTEMPTS_EXHAUSTED`, `REGION_UNRESOLVED`, `EXPIRED`), and
   * `null` on a success. The local audio is still on disk in every failed case.
   */
  errorCode: string | null;
}

/**
 * Boot-time health of offline-recording local storage, as Rust reports it.
 *
 * `available` is false when `Storage::new` was refused at boot — most often
 * because the database on disk was written by a newer build. `errorCode` is the
 * `OFFLINE_*` code that refusal carried, and is `null` both when storage is
 * healthy and when it is absent for a reason boot never established. A `null`
 * code therefore means "no reason given", never "no reason".
 *
 * There is no matching event: the answer is settled before the webview loads
 * and cannot change without restarting on a different build.
 */
export interface OfflineStorageStatus {
  available: boolean;
  errorCode: string | null;
}

export const offlineRecordingBridge = {
  /**
   * Whether offline-recording storage opened at boot. The one command here that
   * does not need `Storage` to be registered — every other one is rejected at
   * Tauri's `State` extraction when it is not, which is the case this reports.
   */
  storageStatus: () => invoke<OfflineStorageStatus>('offline_recording_storage_status'),

  /**
   * Create — or resolve — the local consult this capture records into.
   *
   * Returns a {@link CreateOfflineRecordingResult}, not a bare id: naming a
   * `serverSessionId` this device already holds a consult for resolves to that
   * consult rather than minting a second one, so the caller must read
   * `hasCapturedAudio` before choosing a {@link OfflineCaptureIntent}.
   */
  create: (args: CreateOfflineRecordingArgs) =>
    invoke<CreateOfflineRecordingResult>('offline_recording_create', { args }),

  updateSettings: (
    localSessionId: string,
    settings: UpdateOfflineRecordingSettingsArgs
  ) => invoke<void>('offline_recording_update_settings', { localSessionId, settings }),

  /**
   * Begin capture.
   *
   * `microphone` is the caller's selection **at start time** and takes
   * precedence in Rust over the `offline_sessions.microphone` column the session
   * was created with; omitting it falls back to that column.
   *
   * Passed rather than re-read from the row because the row is written once, at
   * create, and a microphone chosen after that would otherwise never reach the
   * device that gets opened. The online desktop start path passes its input
   * device the same way (`useMutateStartTauriRecording` → `recorderFmc.start`).
   *
   * Sent as an explicit `null` when absent, matching `anonymousAccess.ts`: the
   * key survives serialization, so Rust reads a deliberate "nothing selected"
   * rather than a dropped argument.
   *
   * `intent` says whether this consult is expected to be empty — see
   * {@link OfflineCaptureIntent}. Independent of `microphone`, and both are
   * resolved at start: one decides WHICH DEVICE the capture opens, the other
   * decides WHETHER it may open at all on a consult that already holds audio.
   */
  start: (localSessionId: string, intent: OfflineCaptureIntent, microphone?: string) =>
    invoke<void>('offline_recording_start', {
      localSessionId,
      intent,
      microphone: microphone ?? null,
    }),

  pause: (localSessionId: string) =>
    invoke<void>('offline_recording_pause', { localSessionId }),

  resume: (localSessionId: string) =>
    invoke<void>('offline_recording_resume', { localSessionId }),

  stop: (localSessionId: string) =>
    invoke<void>('offline_recording_stop', { localSessionId }),

  delete: (localSessionId: string) =>
    invoke<void>('offline_recording_delete', { localSessionId }),

  list: () => invoke<OfflineRecordingItem[]>('offline_recording_list'),

  /**
   * Queue a delete for one or more **server** sessions the clinician removed
   * while offline.
   *
   * Note the `server` in the command name: `delete` above removes a LOCAL
   * recording and its audio, this touches no local file and only ever names a
   * session the backend owns.
   *
   * The whole array goes in ONE call. Rust writes it in a single transaction,
   * so a select-all delete either queues entirely or not at all — per-id calls
   * would make a partial queue reachable, which is a half-deleted list.
   *
   * **This rejects rather than silently succeeding**, and callers must rely on
   * that. The clinician's row is hidden optimistically, so a rejection is what
   * tells the mutation to restore it and leave the local audio intact.
   *
   * `userId` and `captureRegion` are deliberately NOT parameters — Rust stamps
   * both from the cached identity, which is what keeps a queued delete private
   * to the clinician who made it and unable to be sent under anyone else's
   * credentials.
   */
  enqueueServerDeletes: (serverSessionIds: string[]) =>
    invoke<void>('offline_recording_server_delete_enqueue', { serverSessionIds }),

  /**
   * The server sessions this clinician still owes the backend a delete for, so
   * the render-time mirror can be reconciled on launch.
   *
   * Ids only, and only those still owed — a delete that has been replayed, has
   * given up, or has been abandoned is simply absent, so its session reappears
   * with no status rule duplicated on this side. Empty for a clinician with
   * nothing queued, and empty for a second clinician on the same machine.
   */
  listServerDeletes: () => invoke<string[]>('offline_recording_server_deletes_list'),

  /**
   * The mid-capture salvage tallies boot recovery wrote that have not yet
   * reached telemetry.
   *
   * **A pull, because the push arrives too early to be heard.** Boot recovery
   * runs inside the Tauri `setup` hook. In a release build the log plugin's
   * only target is the WebView, the JS end of that bridge attaches from a
   * React effect in `_app.tsx`, and Tauri's emit has no replay — so every
   * salvage line recovery writes goes into a bridge with nothing on the far
   * end. Rust persists the counts instead, and this reads them back once this
   * side is up.
   *
   * **`take`, not `get`.** Each returned run is marked reported in the same
   * transaction, so calling twice returns the second run's worth of nothing.
   * Delivery is at-most-once by design: these counts are used to size a defect
   * that silently deletes consult audio, and a duplicated run would argue the
   * problem is twice the size it is.
   *
   * Counts only — no session id, no take index, no path. Nothing in the
   * payload joins to a consult, a patient or a clinician.
   */
  takeSalvageReport: () =>
    invoke<OfflineSalvageRunReport[]>('offline_recording_take_salvage_report'),
};
